import { Highlights, HighlightsItem, HighlightsTextItem } from '@cmg/design-system';
import { useIntl } from '@cmg/i18n';
import React, { FC, Fragment, useState } from 'react';

import { OfferingStatusChip } from '../../../../design-system/components/data-display/offering-status-chip/OfferingStatusChip';
import { OfferingStatus as OfferingStatusEnum, OfferingType, Sector } from '../../../../graphql';
import { sectorLabels } from '../../../constants';
import { Offering_OfferingHeader_OfferingTermsPartsFragment } from '../../graphql';

type Props = {
  issuerName: string;
  type?: OfferingType;
  hasCreateInternationalTrait: boolean;
  isCreateNewOffering: boolean;
  pricingCountryCode?: string | null;
  sector?: Sector | null;
  terms?: Offering_OfferingHeader_OfferingTermsPartsFragment;
  pricingCurrencyCode?: string;
  status?: OfferingStatusEnum;
  offeringTypeLabel: string;
};

export const ConvertibleOfferingHighlights: FC<Props> = ({
  issuerName,
  type,
  isCreateNewOffering,
  sector,
  terms,
  pricingCurrencyCode,
  status,
  offeringTypeLabel,
}) => {
  const intl = useIntl();
  const [latestFilling] = useState(
    terms?.filings.length ? terms.filings[terms.filings.length - 1] : undefined
  );

  const renderHighlightItems = () => {
    if (isCreateNewOffering) return null;

    return (
      <Fragment>
        {type && (
          <HighlightsTextItem
            data-testid="offering-type-highlight"
            name="OFFERING TYPE"
            text={offeringTypeLabel}
          />
        )}
        <HighlightsTextItem name="SECTOR" text={sector ? sectorLabels[sector] : '-'} />
        <HighlightsTextItem
          name="PRINCIPAL AMNT"
          text={intl.formatNumber(latestFilling?.sizeInCurrency, {
            format: 'currencyCompact',
            currency: pricingCurrencyCode,
          })}
        />

        {status === OfferingStatusEnum.Priced ? (
          <React.Fragment>
            <HighlightsTextItem
              name="COUPON"
              text={intl.formatNumber(latestFilling?.coupon, {
                format: 'percent',
                maximumFractionDigits: 4,
                minimumFractionDigits: 4,
              })}
            />
            <HighlightsTextItem
              name="PREMIUM"
              text={intl.formatNumber(latestFilling?.premium, {
                format: 'percent',
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })}
            />
          </React.Fragment>
        ) : (
          <React.Fragment>
            <HighlightsTextItem
              name="COUPON TALK"
              text={
                latestFilling?.couponTalkLow || latestFilling?.couponTalkHigh
                  ? `${intl.formatNumber(latestFilling.couponTalkLow, {
                      format: 'percent',
                      maximumFractionDigits: 4,
                      minimumFractionDigits: 4,
                    })}–${intl.formatNumber(latestFilling.couponTalkHigh, {
                      format: 'percent',
                      maximumFractionDigits: 4,
                      minimumFractionDigits: 4,
                    })}`
                  : '-'
              }
            />
            <HighlightsTextItem
              name="PREMIUM TALK"
              text={
                latestFilling?.premiumTalkLow || latestFilling?.premiumTalkHigh
                  ? `${intl.formatNumber(latestFilling.premiumTalkLow, {
                      format: 'percent',
                    })}–${intl.formatNumber(latestFilling.premiumTalkHigh, {
                      format: 'percent',
                    })}`
                  : '-'
              }
            />
          </React.Fragment>
        )}

        {status && <HighlightsItem name="STATUS" value={<OfferingStatusChip status={status} />} />}
      </Fragment>
    );
  };

  return (
    <Highlights
      data-testid="offering-header-highlights"
      title={issuerName}
      titleItems={renderHighlightItems()}
    />
  );
};
